import {createContext, useContext} from 'react';
import type {CheckoutHostContextValue, CheckoutRequest} from '@Components/checkout-flow/checkout-flow.types';
import {isModernCheckoutEnabled} from '@Components/checkout-flow/checkout-flow.library';
import {openCheckout} from '@Modals/checkout-modal/checkout-modal.library';

export const checkoutHostContext = createContext<CheckoutHostContextValue | null>(null);

/**
 * Opens checkout inside the surrounding host modal when one provides it, and falls back to the standalone
 * checkout modal (or the legacy dialog when the flag is off) everywhere else.
 */
export const useOpenCheckout = (): ((request: CheckoutRequest) => void) => {
  const host = useContext(checkoutHostContext);
  return (request: CheckoutRequest): void => {
    if (host !== null && isModernCheckoutEnabled()) {
      host.startCheckout(request);
      return;
    }
    openCheckout(request);
  };
};

/**
 * Whether checkout will open inside the surrounding modal rather than replacing it. A modal that hosts
 * checkout must stay put; one that does not has to close itself, or the dialog opens on top of it.
 */
export const useIsCheckoutHosted = (): boolean => {
  const host = useContext(checkoutHostContext);
  return host !== null && isModernCheckoutEnabled();
};
